import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";

const emptyForm = {
  name: "", email: "", phone: "", status: "active",
  address: { zipcode: "", prefecture: "", city: "", address1: "", address2: "", prefecture_romaji: "", city_romaji: "", address1_romaji: "", address2_romaji: "" },
};

export default function CustomerForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) { api.get(`/customers/${id}`).then((r) => setForm(r.data)).catch(() => navigate("/crm/customers")); }
  }, [id]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleAddressChange = (field, value) => {
    setForm((prev) => {
      const newAddress = { ...prev.address, [field]: value };
      if (field === "address2") newAddress.address2_romaji = value;
      return { ...prev, address: newAddress };
    });
  };

  const lookupZipcode = async () => {
    const zip = form.address.zipcode.replace(/\D/g, "");
    if (zip.length !== 7) return;
    setZipLoading(true);
    try {
      const { data } = await api.get(`/address/zipcode/${zip}`);
      setForm((prev) => ({
        ...prev,
        address: { ...prev.address, prefecture: data.prefecture, city: data.city, address1: data.address1, prefecture_romaji: data.prefecture_romaji || "", city_romaji: data.city_romaji || "", address1_romaji: data.address1_romaji || "" },
      }));
    } catch { /* */ }
    setZipLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      if (isEdit) await api.put(`/customers/${id}`, form);
      else await api.post("/customers", form);
      navigate("/crm/customers");
    } catch { setError("Erro ao salvar cliente. Verifique os dados."); }
    setSaving(false);
  };

  return (
    <div data-testid="customer-form-page">
      <button onClick={() => navigate("/crm/customers")} className="inline-flex items-center gap-2 text-sm mb-6 transition-colors" style={{ color: "var(--crm-text-muted)" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="crm-heading mb-8">{isEdit ? "Editar Cliente" : "Novo Cliente"}</h1>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {error && <div className="p-4 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}

        <div className="crm-card p-6 space-y-5">
          <h2 className="crm-subheading">Informações Básicas</h2>
          <div>
            <label className="crm-label">Nome *</label>
            <input required value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="customer-name-input" className="crm-input" placeholder="Nome completo" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="crm-label">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="customer-email-input" className="crm-input" placeholder="email@exemplo.com" />
            </div>
            <div>
              <label className="crm-label">Telefone</label>
              <input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} data-testid="customer-phone-input" className="crm-input" placeholder="090-XXXX-XXXX" />
            </div>
          </div>
          <div>
            <label className="crm-label">Status</label>
            <select value={form.status} onChange={(e) => handleChange("status", e.target.value)} data-testid="customer-status-select" className="crm-input">
              <option value="active">Ativo</option><option value="inactive">Inativo</option><option value="blocked">Bloqueado</option>
            </select>
          </div>
        </div>

        <div className="crm-card p-6 space-y-5">
          <h2 className="crm-subheading">Endereço (Japonês)</h2>
          <div>
            <label className="crm-label">CEP (〒)</label>
            <div className="flex gap-2">
              <input value={form.address?.zipcode || ""} onChange={(e) => handleAddressChange("zipcode", e.target.value)} data-testid="customer-zipcode-input" className="crm-input" placeholder="1234567" maxLength={8} />
              <button type="button" onClick={lookupZipcode} disabled={zipLoading} data-testid="zipcode-lookup-btn" className="crm-btn-secondary whitespace-nowrap">
                {zipLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="crm-label">Prefeitura (都道府県)</label>
              <input value={form.address?.prefecture || ""} onChange={(e) => handleAddressChange("prefecture", e.target.value)} data-testid="customer-prefecture-input" className="crm-input" placeholder="東京都" />
            </div>
            <div>
              <label className="crm-label">Cidade (市区町村)</label>
              <input value={form.address?.city || ""} onChange={(e) => handleAddressChange("city", e.target.value)} data-testid="customer-city-input" className="crm-input" placeholder="新宿区" />
            </div>
          </div>
          <div>
            <label className="crm-label">Endereço 1</label>
            <input value={form.address?.address1 || ""} onChange={(e) => handleAddressChange("address1", e.target.value)} data-testid="customer-address1-input" className="crm-input" placeholder="町名・番地" />
          </div>
          <div>
            <label className="crm-label">Endereço 2 (complemento)</label>
            <input value={form.address?.address2 || ""} onChange={(e) => handleAddressChange("address2", e.target.value)} data-testid="customer-address2-input" className="crm-input" placeholder="建物名・部屋番号" />
          </div>
        </div>

        <div className="crm-card p-6 space-y-5">
          <h2 className="crm-subheading">Endereço (Romaji)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="crm-label">Prefeitura (Romaji)</label>
              <input value={form.address?.prefecture_romaji || ""} onChange={(e) => handleAddressChange("prefecture_romaji", e.target.value)} data-testid="customer-prefecture-romaji-input" className="crm-input" placeholder="Tokyo-to" />
            </div>
            <div>
              <label className="crm-label">Cidade (Romaji)</label>
              <input value={form.address?.city_romaji || ""} onChange={(e) => handleAddressChange("city_romaji", e.target.value)} data-testid="customer-city-romaji-input" className="crm-input" placeholder="Shinjuku-ku" />
            </div>
          </div>
          <div>
            <label className="crm-label">Endereço 1 (Romaji)</label>
            <input value={form.address?.address1_romaji || ""} onChange={(e) => handleAddressChange("address1_romaji", e.target.value)} data-testid="customer-address1-romaji-input" className="crm-input" placeholder="Nishishinjuku" />
          </div>
          <div>
            <label className="crm-label">Endereço 2 - complemento (Romaji)</label>
            <input value={form.address?.address2_romaji || ""} onChange={(e) => handleAddressChange("address2_romaji", e.target.value)} data-testid="customer-address2-romaji-input" className="crm-input" placeholder="Building 101" />
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="customer-save-btn" className="crm-btn-primary">
            {saving ? "Salvando..." : isEdit ? "Atualizar" : "Criar Cliente"}
          </button>
          <button type="button" onClick={() => navigate("/crm/customers")} className="crm-btn-secondary">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
